import React, { useEffect, useMemo, useState, useRef } from "react";
import Card from "components/card";
import {
  IoEye,
  IoCall,
  IoTrash,
  IoFilter,
  IoChevronBack,
  IoChevronForward,
  IoCalendar,
  IoSearch,
} from "react-icons/io5";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import LeadPriorityBadge from "components/badges/LeadPriorityBadge";
import LeadFilters from "components/filters/LeadFilters";
import SortDropdown from "components/dropdown/SortDropdown";
import FolderDropdown from "components/dropdown/FolderDropdown";
import { API_BASE_URL } from "utils/api";
import {
  addClientNote,
  addClientSchedule,
  deleteClientNote,
  deleteClientSchedule,
  getClientNoteScheduleDetails,
} from "../clientApi";
import { useNotificationContext } from "context/NotificationContext";
import usePermission from "hooks/usePermission";

const columnHelper = createColumnHelper();
const CALL_ACTIVITY_STORAGE_KEY = "client_call_activity";

export default function ClientComplexTable({
  tableData,
  showToast,
  isLoading,
  error,
  filters,
  onFilterChange,
  onFiltersChange,
  onClearFilters,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  total,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshNotifications } = useNotificationContext();
  const { can } = usePermission();
  const canDeleteClient = can("client_management", "delete");
  const [sorting, setSorting] = React.useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = React.useMemo(() => {
    if (!filters) return 0;
    try {
      return Object.entries(filters).filter(([k, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (Array.isArray(v)) return v.length > 0;
        return true;
      }).length;
    } catch (e) {
      return 0;
    }
  }, [filters]);
  const tableWrapperRef = useRef(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isCallerModalOpen, setIsCallerModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [noteErrors, setNoteErrors] = useState({});
  const [noteSuccessMessage, setNoteSuccessMessage] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [scheduleSuccessMessage, setScheduleSuccessMessage] = useState("");
  const [isAddingSchedule, setIsAddingSchedule] = useState(false);
  const [isFetchingActivityDetails, setIsFetchingActivityDetails] = useState(false);
  const [activityFetchError, setActivityFetchError] = useState("");
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);
  const [deletingActivityId, setDeletingActivityId] = useState("");
  const [activityDeleteStatus, setActivityDeleteStatus] = useState({ type: "", message: "" });
  const [activityDeleteConfirm, setActivityDeleteConfirm] = useState({
    show: false,
    type: null,
    itemId: "",
    title: "",
    message: "",
  });
  const [clientDeleteConfirm, setClientDeleteConfirm] = useState({
    show: false,
    clientId: "",
    clientName: "",
  });
  const [isDeletingClient, setIsDeletingClient] = useState(false);
  const [activityByClient, setActivityByClient] = useState({});
  const [isUpdatingCallStatus, setIsUpdatingCallStatus] = useState(false);
  const [hasCheckedWaitingOnEntry, setHasCheckedWaitingOnEntry] = useState(false);
  const [isCheckingWaitingClient, setIsCheckingWaitingClient] = useState(false);

  const toDisplayClient = (item, index) => {
    const nameSlug = (item.name || `client-${index + 1}`)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ".");

    return {
      ...item,
      id: item.id || item._id || `client-${index + 1}`,
      phone: item.phone || item.phoneNumber || "N/A",
      email: item.email || `${nameSlug}@example.com`,
    };
  };

  const [data, setData] = useState(() =>
    tableData.map((item, index) => toDisplayClient(item, index))
  );

  useEffect(() => {
    setData(tableData.map((item, index) => toDisplayClient(item, index)));
  }, [tableData]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CALL_ACTIVITY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setActivityByClient(parsed);
        }
      }
    } catch (storageError) {
      console.error("Failed to read client call activity from storage", storageError);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CALL_ACTIVITY_STORAGE_KEY, JSON.stringify(activityByClient));
    } catch (storageError) {
      console.error("Failed to save client call activity", storageError);
    }
  }, [activityByClient]);

  useEffect(() => {
    const normalizedPath = (location?.pathname || "").replace(/\/$/, "");
    if (normalizedPath === "/admin/client-management") {
      setHasCheckedWaitingOnEntry(false);
    }
  }, [location?.pathname]);

  const getClientKey = (client) => {
    if (!client) return "";
    return String(client.id || client._id || client.phone || client.email || client.name || "unknown");
  };

  const getClientActivity = (client) => {
    const key = getClientKey(client);
    return activityByClient[key] || { notes: [], schedules: [] };
  };

  const getCallingStatus = (client) => {
    const candidates = [
      client?.call_status,
      client?.callStatus,
      client?.calling_status,
      client?.callingStatus,
      client?.call_status?.status,
      client?.callStatus?.status,
      client?.calling_status?.status,
      client?.callingStatus?.status,
      client?.call_status?.value,
      client?.callStatus?.value,
      client?.calling_status?.value,
      client?.callingStatus?.value,
    ];

    const firstString = candidates.find((item) => typeof item === "string" && item.trim());
    return firstString ? firstString.trim().toLowerCase() : "";
  };

  // const formatCallStatus = (value) => {
  //   const normalized = String(value || "").trim().toLowerCase();
  //   if (!normalized) return "-";
  //   return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
  // };

  const getClientsFromResponse = (responseData) => {
    const possibleCollections = [
      responseData?.data?.data,
      responseData?.data?.allClients?.data,
      responseData?.data?.allClients,
      responseData?.data,
      responseData,
    ];

    for (const collection of possibleCollections) {
      if (Array.isArray(collection)) return collection;
    }

    return [];
  };

  const normalizeNoteErrorPath = (path = "") => {
    const normalized = String(path).toLowerCase().replace(/[^a-z]/g, "");
    if (normalized.includes("clientid")) return "clientId";
    if (normalized.includes("note")) return "note";
    return "submit";
  };

  const mapNoteApiErrors = (apiErrors = []) => {
    if (!Array.isArray(apiErrors)) return {};

    return apiErrors.reduce((acc, item) => {
      const fieldKey = normalizeNoteErrorPath(item?.path);
      const message = item?.msg || item?.message;
      if (!message) return acc;

      acc[fieldKey] = acc[fieldKey] ? `${acc[fieldKey]} ${message}` : message;
      return acc;
    }, {});
  };

  const normalizeScheduleErrorPath = (path = "") => {
    const normalized = String(path).toLowerCase().replace(/[^a-z]/g, "");
    if (normalized.includes("clientid")) return "clientId";
    if (normalized.includes("scheduledtime") || normalized.includes("scheduledfor")) {
      return "scheduledTime";
    }
    if (normalized.includes("note")) return "note";
    return "submit";
  };

  const mapScheduleApiErrors = (apiErrors = []) => {
    if (!Array.isArray(apiErrors)) return {};

    return apiErrors.reduce((acc, item) => {
      const fieldKey = normalizeScheduleErrorPath(item?.path);
      const message = item?.msg || item?.message;
      if (!message) return acc;

      acc[fieldKey] = acc[fieldKey] ? `${acc[fieldKey]} ${message}` : message;
      return acc;
    }, {});
  };

  const getScheduleFromApiData = (payload, fallbackSchedule) => {
    const record =
      payload?.data?.schedule ||
      payload?.data?.scheduledCall ||
      payload?.data ||
      payload?.schedule ||
      null;

    return {
      id:
        record?._id ||
        record?.id ||
        `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      scheduleId: record?._id || record?.id || "",
      scheduledFor:
        record?.scheduledFor ||
        record?.scheduledTime ||
        fallbackSchedule.scheduledFor,
      note: record?.note || fallbackSchedule.note,
      createdAt: record?.createdAt || new Date().toISOString(),
    };
  };

  const normalizeActivityDetails = (payload) => {
    const notes = Array.isArray(payload?.notes)
      ? payload.notes
        .map((item, index) => {
          if (!item || !item.note) return null;
          const createdAt = item?.date || item?.createdAt || new Date().toISOString();
          return {
            id: item?._id || `${createdAt}-${index}-${Math.random().toString(36).slice(2, 7)}`,
            noteId: item?._id || item?.id || "",
            content: item.note,
            createdAt,
          };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      : [];

    const schedules = Array.isArray(payload?.schedules)
      ? payload.schedules
        .map((item, index) => {
          if (!item) return null;
          const scheduledFor = item?.scheduledTime || item?.scheduledFor;
          if (!scheduledFor) return null;

          return {
            id:
              item?._id ||
              `${scheduledFor}-${item?.createdAt || Date.now()}-${index}-${Math.random()
                .toString(36)
                .slice(2, 7)}`,
            scheduleId: item?._id || item?.id || "",
            scheduledFor,
            note: item?.note || "",
            createdAt: item?.createdAt || new Date().toISOString(),
            scheduleStatus: item?.scheduleStatus || "",
          };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.scheduledFor) - new Date(a.scheduledFor))
      : [];

    return { notes, schedules };
  };

  const launchDialer = (phone) => {
    if (!phone || phone === "N/A") {
      if (showToast) {
        showToast("Phone number not found for this client", "error");
      }
      return;
    }

    window.location.href = `tel:${String(phone).trim()}`;
  };

  const openCallPopup = (client) => {
    setSelectedClient(client);
    setIsCallerModalOpen(true);
    setIsActivityModalOpen(false);
    setNoteInput("");
    setNoteErrors({});
    setNoteSuccessMessage("");
    setScheduleDateTime("");
    setScheduleNote("");
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setActivityDeleteStatus({ type: "", message: "" });
  };

  const updateClientCallStatus = async ({ clientId, nextStatus, showSuccessToast = true }) => {
    if (!clientId || !nextStatus || isUpdatingCallStatus) return false;

    setIsUpdatingCallStatus(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/clients/call-status`,
        {
          clientId,
          call_status: nextStatus,
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        }
      );

      const persistedStatus = response?.data?.data?.call_status || nextStatus;

      setData((prev) =>
        prev.map((client) => {
          const currentId = client.id || client._id;
          return String(currentId) === String(clientId)
            ? {
              ...client,
              call_status: persistedStatus,
            }
            : client;
        })
      );

      setSelectedClient((prev) => {
        if (!prev) return prev;
        const prevId = prev.id || prev._id;
        if (String(prevId) !== String(clientId)) return prev;
        return {
          ...prev,
          call_status: persistedStatus,
        };
      });

      if (showSuccessToast && showToast) {
        const successMessage = response?.data?.message || `Call status updated to ${nextStatus}`;
        showToast(successMessage, "success");
      }

      if (onRefresh) {
        await onRefresh();
      }

      return true;
    } catch (err) {
      const errorData = err?.response?.data;
      const validationMessages = Array.isArray(errorData?.errors)
        ? errorData.errors.map((item) => item?.msg).filter(Boolean)
        : [];
      const errorMessage = validationMessages.length
        ? validationMessages.join("\n")
        : errorData?.message || "Failed to update call status";

      if (showToast) {
        showToast(errorMessage, "error");
      }

      return false;
    } finally {
      setIsUpdatingCallStatus(false);
    }
  };

  const handleCallClient = async (client) => {
    openCallPopup(client);
    const clientId = client?.id || client?._id;
    await updateClientCallStatus({
      clientId,
      nextStatus: "waiting",
      showSuccessToast: false,
    });
    launchDialer(client?.phone);
  };

  const updateCallStatus = async (nextStatus, { closeOnSuccess = false } = {}) => {
    if (!selectedClient) return;

    const clientId = selectedClient.id || selectedClient._id;
    if (!clientId) {
      if (showToast) showToast("Unable to update call status", "error");
      return;
    }

    const updated = await updateClientCallStatus({ clientId, nextStatus });
    if (updated && closeOnSuccess) {
      closeCallFlow();
    }
  };

  useEffect(() => {
    const normalizedPath = (location?.pathname || "").replace(/\/$/, "");
    if (normalizedPath !== "/admin/client-management") return;
    if (hasCheckedWaitingOnEntry || isCheckingWaitingClient || isLoading) return;

    const waitingOnCurrentData = Array.isArray(tableData)
      ? tableData.find((client) => getCallingStatus(client) === "waiting")
      : Array.isArray(data)
      ? data.find((client) => getCallingStatus(client) === "waiting")
      : null;

    if (waitingOnCurrentData) {
      openCallPopup(waitingOnCurrentData);
      setHasCheckedWaitingOnEntry(true);
      return;
    }

    const findWaitingClientFromApi = async () => {
      setIsCheckingWaitingClient(true);
      try {
        const token = localStorage.getItem("token");

        const requestVariants = [
          { page: 1, limit: 1, call_status: "waiting" },
          { page: 1, limit: 1, calling_status: "waiting" },
          { page: 1, limit: 200 },
        ];

        let waitingClient = null;
        for (const params of requestVariants) {
          const response = await axios.get(`${API_BASE_URL}/clients`, {
            params,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          const clients = getClientsFromResponse(response?.data);
          if (!Array.isArray(clients) || clients.length === 0) continue;

          waitingClient = clients.find((client) => getCallingStatus(client) === "waiting");
          if (waitingClient) break;
        }

        if (waitingClient) {
          openCallPopup(waitingClient);
        }
      } catch (err) {
        // Ignore waiting lookup errors to avoid interrupting client page load.
      } finally {
        setHasCheckedWaitingOnEntry(true);
        setIsCheckingWaitingClient(false);
      }
    };

    findWaitingClientFromApi();
  }, [
    location?.pathname,
    tableData,
    data,
    isLoading,
    hasCheckedWaitingOnEntry,
    isCheckingWaitingClient,
  ]);

  const closeCallFlow = () => {
    setIsCallerModalOpen(false);
    setIsActivityModalOpen(false);
    setSelectedClient(null);
    setNoteInput("");
    setNoteErrors({});
    setNoteSuccessMessage("");
    setIsAddingNote(false);
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setIsAddingSchedule(false);
    setIsDeletingActivity(false);
    setDeletingActivityId("");
    setActivityDeleteStatus({ type: "", message: "" });
    setActivityDeleteConfirm({ show: false, type: null, itemId: "", title: "", message: "" });
  };

  const openActivityModal = async () => {
    if (!selectedClient) return;

    const clientId = selectedClient.id || selectedClient._id;
    const clientKey = getClientKey(selectedClient);

    setIsCallerModalOpen(false);
    setIsActivityModalOpen(true);
    setNoteErrors({});
    setNoteSuccessMessage("");
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setActivityFetchError("");
    setActivityDeleteStatus({ type: "", message: "" });

    if (!clientId) {
      setActivityFetchError("Client ID is required to fetch activity details");
      return;
    }

    setIsFetchingActivityDetails(true);
    try {
      const responseData = await getClientNoteScheduleDetails(clientId);
      const normalizedActivity = normalizeActivityDetails(responseData?.data || {});

      setActivityByClient((prev) => ({
        ...prev,
        [clientKey]: normalizedActivity,
      }));
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to load notes and schedules for this client";
      setActivityFetchError(errorMessage);
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsFetchingActivityDetails(false);
    }
  };

  const addNote = async () => {
    if (!selectedClient || isAddingNote) return;

    const clientId = selectedClient.id || selectedClient._id;
    const value = noteInput.trim();

    if (!clientId) {
      setNoteErrors({ clientId: "Client ID is required" });
      return;
    }

    if (!value) {
      setNoteErrors({ note: "Note is required" });
      return;
    }

    const clientKey = getClientKey(selectedClient);
    const current = getClientActivity(selectedClient);

    setIsAddingNote(true);
    setNoteErrors({});
    setNoteSuccessMessage("");

    try {
      const responseData = await addClientNote({ clientId, note: value });
      const notesFromApi = responseData?.data?.notes;

      const normalizedApiNotes = Array.isArray(notesFromApi)
        ? notesFromApi
          .map((item) => {
            if (!item || !item.note) return null;
            return {
              id:
                item?._id ||
                `${item?.date || Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              noteId: item?._id || item?.id || "",
              content: item.note,
              createdAt: item.date || new Date().toISOString(),
            };
          })
          .filter(Boolean)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : null;

      const fallbackNotes = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          content: value,
          createdAt: new Date().toISOString(),
        },
        ...current.notes,
      ];

      setActivityByClient((prev) => ({
        ...prev,
        [clientKey]: {
          notes:
            normalizedApiNotes && normalizedApiNotes.length > 0
              ? normalizedApiNotes
              : fallbackNotes,
          schedules: current.schedules,
        },
      }));

      setNoteInput("");
      setNoteSuccessMessage(responseData?.message || "Client note added successfully");

      if (showToast) {
        showToast(responseData?.message || "Client note added successfully", "success");
      }
    } catch (err) {
      const errorData = err?.response?.data;
      const mappedErrors = mapNoteApiErrors(errorData?.errors);

      if (Object.keys(mappedErrors).length > 0) {
        setNoteErrors(mappedErrors);
      } else {
        const fallbackMessage = errorData?.message || "Failed to add note";
        setNoteErrors({ submit: fallbackMessage });
      }

      if (showToast) {
        showToast(errorData?.message || "Failed to add note", "error");
      }
    } finally {
      setIsAddingNote(false);
    }
  };

  const requestActivityDelete = ({ type, itemId }) => {
    if (!selectedClient || !itemId || !type) return;

    setActivityDeleteConfirm({
      show: true,
      type,
      itemId,
      title: type === "note" ? "Delete Note?" : "Delete Schedule?",
      message:
        type === "note"
          ? "This note will be removed from this call activity timeline."
          : "This schedule entry will be removed from this client timeline.",
    });
  };

  const closeActivityDeleteConfirm = () => {
    setActivityDeleteConfirm({ show: false, type: null, itemId: "", title: "", message: "" });
  };

  const confirmActivityDelete = async () => {
    if (!selectedClient || !activityDeleteConfirm?.itemId || !activityDeleteConfirm?.type) {
      closeActivityDeleteConfirm();
      return;
    }

    const clientId = selectedClient.id || selectedClient._id;
    if (!clientId) {
      setActivityDeleteStatus({ type: "error", message: "Client ID is missing for delete operation" });
      closeActivityDeleteConfirm();
      return;
    }

    setIsDeletingActivity(true);
    setDeletingActivityId(activityDeleteConfirm.itemId);

    const clientKey = getClientKey(selectedClient);
    const current = getClientActivity(selectedClient);

    try {
      if (activityDeleteConfirm.type === "note") {
        await deleteClientNote(clientId, activityDeleteConfirm.itemId);
        const nextNotes = current.notes.filter(
          (item) => item.noteId !== activityDeleteConfirm.itemId && item.id !== activityDeleteConfirm.itemId
        );
        setActivityByClient((prev) => ({
          ...prev,
          [clientKey]: {
            notes: nextNotes,
            schedules: current.schedules,
          },
        }));
        setActivityDeleteStatus({ type: "success", message: "Note deleted successfully" });
        setNoteSuccessMessage("Note deleted successfully");
        if (showToast) {
          showToast("Note deleted successfully", "success");
        }
      }

      if (activityDeleteConfirm.type === "schedule") {
        await deleteClientSchedule(clientId, activityDeleteConfirm.itemId);
        const nextSchedules = current.schedules.filter(
          (item) => item.scheduleId !== activityDeleteConfirm.itemId && item.id !== activityDeleteConfirm.itemId
        );
        setActivityByClient((prev) => ({
          ...prev,
          [clientKey]: {
            notes: current.notes,
            schedules: nextSchedules,
          },
        }));
        setActivityDeleteStatus({ type: "success", message: "Schedule deleted successfully" });
        setScheduleSuccessMessage("Schedule deleted successfully");
        if (showToast) {
          showToast("Schedule deleted successfully", "success");
        }
        // Refresh notifications after deleting a schedule
        await refreshNotifications();
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to delete item";
      setActivityDeleteStatus({ type: "error", message: errorMessage });
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsDeletingActivity(false);
      setDeletingActivityId("");
      closeActivityDeleteConfirm();
    }
  };

  const addSchedule = async () => {
    if (!selectedClient || isAddingSchedule) return;

    const clientId = selectedClient.id || selectedClient._id;
    if (!clientId) {
      setScheduleErrors({ clientId: "Client ID is required" });
      return;
    }

    if (!scheduleDateTime) {
      setScheduleErrors({ scheduledTime: "scheduledTime is required" });
      return;
    }

    const clientKey = getClientKey(selectedClient);
    const current = getClientActivity(selectedClient);
    const payload = {
      clientId,
      scheduledTime: new Date(scheduleDateTime).toISOString(),
      note: scheduleNote.trim(),
    };

    const fallbackSchedule = {
      scheduledFor: payload.scheduledTime,
      note: payload.note,
    };

    setIsAddingSchedule(true);
    setScheduleErrors({});
    setScheduleSuccessMessage("");

    try {
      const responseData = await addClientSchedule(payload);
      const createdSchedule = getScheduleFromApiData(responseData, fallbackSchedule);

      setActivityByClient((prev) => ({
        ...prev,
        [clientKey]: {
          notes: current.notes,
          schedules: [createdSchedule, ...current.schedules],
        },
      }));

      setScheduleSuccessMessage(responseData?.message || "Schedule added successfully");
      setScheduleDateTime("");
      setScheduleNote("");

      if (showToast) {
        showToast(responseData?.message || "Schedule added successfully", "success");
      }

      const statusUpdated = await updateClientCallStatus({
        clientId,
        nextStatus: "scheduled",
        showSuccessToast: false,
      });

      if (statusUpdated) {
        // Refresh notifications immediately after scheduling a call
        // This updates the notification count and list in real-time
        await refreshNotifications();
        closeCallFlow();
      }
    } catch (err) {
      const errorData = err?.response?.data;
      const mappedErrors = mapScheduleApiErrors(errorData?.errors);

      if (Object.keys(mappedErrors).length > 0) {
        setScheduleErrors(mappedErrors);
      } else {
        const fallbackMessage = errorData?.message || "Failed to add schedule";
        setScheduleErrors({ submit: fallbackMessage });
      }

      if (showToast) {
        showToast(errorData?.message || "Failed to add schedule", "error");
      }
    } finally {
      setIsAddingSchedule(false);
    }
  };

  const filteredData = useMemo(() => {
    const searchKey = (filters.search || "").trim().toLowerCase();

    return data.filter((client) => {
      const matchesSearch =
        !searchKey ||
        client.name?.toLowerCase().includes(searchKey) ||
        client.country?.toLowerCase().includes(searchKey) ||
        client.email?.toLowerCase().includes(searchKey) ||
        client.phone?.toLowerCase().includes(searchKey);

      const matchesCountry = !filters.country || client.country === filters.country;
      const matchesState = !filters.state || client.state === filters.state;
      const matchesDistrict = !filters.district || client.district === filters.district;
      const matchesPriority = !filters.priority || client.priority === filters.priority;

      return (
        matchesSearch &&
        matchesCountry &&
        matchesState &&
        matchesDistrict &&
        matchesPriority
      );
    });
  }, [data, filters]);

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const requestClientDelete = (client) => {
    if (!client || !client.id) return;
    setClientDeleteConfirm({
      show: true,
      clientId: client.id,
      clientName: client.name || "Unknown Client",
    });
  };

  const closeClientDeleteConfirm = () => {
    setClientDeleteConfirm({ show: false, clientId: "", clientName: "" });
  };

  const confirmClientDelete = async () => {
    if (!clientDeleteConfirm?.clientId) {
      closeClientDeleteConfirm();
      return;
    }

    setIsDeletingClient(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/clients/${clientDeleteConfirm.clientId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      });

      // Update table data
      setData((prev) => prev.filter((client) => client.id !== clientDeleteConfirm.clientId));

      if (showToast) {
        showToast("Client deleted successfully", "success");
      }

      if (onRefresh) {
        await onRefresh();
      }

      closeClientDeleteConfirm();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to delete client";
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsDeletingClient(false);
    }
  };

  const handleDeleteClient = (client) => {
    requestClientDelete(client);
  };

  const handleViewClient = (client) => {
    navigate(`/admin/client-management/client-profile/${client.id}`, { state: { from: location.pathname } });
  };

  const uniqueCountries = useMemo(
    () => Array.from(new Set(data.map((x) => x.country).filter(Boolean))).sort(),
    [data]
  );
  const uniqueStates = useMemo(
    () => Array.from(new Set(data.map((x) => x.state).filter(Boolean))).sort(),
    [data]
  );
  const uniqueDistricts = useMemo(
    () => Array.from(new Set(data.map((x) => x.district).filter(Boolean))).sort(),
    [data]
  );
  const uniquePriorities = useMemo(
    () => Array.from(new Set(data.map((x) => x.priority).filter(Boolean))).sort(),
    [data]
  );

  const currentClientActivity = getClientActivity(selectedClient);

  const getInitials = (name) => {
    if (!name) return "NA";
    const parts = String(name).trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  };

  const renderDismissibleLabel = ({ message, type = "success", onClose, className = "mt-2" }) => {
    if (!message) return null;

    const isError = type === "error";
    const toneClassName =
      isError
        ? "border-l-4 border-red-500 border-y border-r border-red-300 bg-red-100 text-red-800 shadow-sm dark:border-red-400 dark:bg-red-500/20 dark:text-red-200"
        : "border-l-4 border-emerald-500 border-y border-r border-emerald-300 bg-emerald-100 text-emerald-800 shadow-sm dark:border-emerald-400 dark:bg-emerald-500/20 dark:text-emerald-200";

    return (
      <div className={`${className} flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${toneClassName}`}>
        <div className="flex items-start gap-2">
          <span
            className={`mt-[2px] inline-flex min-w-[54px] items-center justify-center rounded-full px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide ${isError
              ? "bg-red-600 text-white dark:bg-red-500"
              : "bg-emerald-600 text-white dark:bg-emerald-500"
              }`}
          >
            {isError ? "Failed" : "Success"}
          </span>
          <span>{message}</span>
        </div>
        {onClose ? (
          <button
            onClick={onClose}
            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold opacity-90 hover:bg-black/10 hover:opacity-100"
            aria-label="Close status"
            title="Close"
          >
            x
          </button>
        ) : null}
      </div>
    );
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">NAME</p>,
      cell: (info) => <p className="text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">EMAIL</p>,
      cell: (info) => <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">PHONE</p>,
      cell: (info) => <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),
    columnHelper.accessor("country", {
      id: "country",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">COUNTRY</p>,
      cell: (info) => <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue()}</p>,
    }),

    columnHelper.accessor("priority", {
      id: "priority",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">PRIORITY</p>,
      cell: (info) => (
        <div className="flex items-center">
          <LeadPriorityBadge priority={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">ACTION</p>,
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCallClient(info.row.original)}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg dark:text-emerald-400 dark:hover:bg-emerald-900/20 transition-colors"
            title="Call Client"
          >
            <IoCall className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleViewClient(info.row.original)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
            title="View Client"
          >
            <IoEye className="h-4 w-4" />
          </button>
          {canDeleteClient && (
            <button
              onClick={() => handleDeleteClient(info.row.original)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              title="Delete Client"
            >
              <IoTrash className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between pt-4">
          <div className="flex flex-wrap items-center gap-4 min-w-0">
            <div className="text-xl font-bold text-navy-700 dark:text-white whitespace-nowrap">Client Details</div>
            <div className="min-w-[260px] max-w-sm flex-1">
              <div className="relative">
                <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={filters.search || ""}
                  onChange={(e) => onFilterChange("search", e.target.value)}
                  placeholder="Search name, email or phone"
                  className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <FolderDropdown 
              type="Client"
              value={filters?.folderId || ""}
              onChange={(val) => onFilterChange("folderId", val)}
            />
            <SortDropdown
              value={filters?.sort || "latest_updated"}
              onChange={(val) => onFilterChange("sort", val)}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showFilters
                ? "bg-brand-500 text-white hover:bg-brand-600"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                }`}
            >
              <IoFilter className="h-5 w-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {typeof total === 'number' ? total : activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {showFilters && (
          <LeadFilters
            filters={filters}
            onFilterChange={onFilterChange}
            onFiltersChange={onFiltersChange}
            onClearFilters={handleClearFilters}
            uniqueCountries={uniqueCountries}
            uniqueStates={uniqueStates}
            uniqueDistricts={uniqueDistricts}
            uniquePriorities={uniquePriorities}
            showSearch={false}
          />
        )}

        <div ref={tableWrapperRef} className={`relative mt-4 overflow-x-scroll xl:overflow-x-hidden ${table.getRowModel().rows.length === 0 ? 'min-h-[180px]' : ''}`}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="!border-px !border-gray-400">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 ${header.id === "actions"
                        ? "sticky right-0 bg-white dark:bg-gray-800 z-10 w-24 text-center"
                        : "text-start cursor-pointer"
                        }`}
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Loading clients...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-red-500 dark:text-red-300">
                    {error}
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No clients found matching your criteria.
                  </td>
                </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    let priorityClass = "hover:bg-gray-50 dark:hover:bg-gray-800";
                    if (row.original.priority === "Urgent") {
                      priorityClass = "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
                    } else if (row.original.priority === "High") {
                      priorityClass = "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30";
                    }
                    return (
                      <tr key={row.id} className={priorityClass}>
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`border-white/0 py-3 pr-4 ${cell.column.id === "actions"
                              ? "sticky right-0 bg-white dark:bg-gray-800 z-10 w-24 text-center"
                              : "min-w-[150px]"
                              }`}
                          >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-center mt-6 gap-6">
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-full p-1">
            <span className="text-sm text-gray-600 dark:text-gray-400 mr-2 px-2">Rows:</span>
            {[10, 20, 50].map((size) => (
              <button
                key={size}
                onClick={() => onPageSizeChange(size)}
                className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${pageSize === size
                  ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  }`}
              >
                {size}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronBack className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronForward className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      {isCallerModalOpen && selectedClient && (
        <div
          className="fixed inset-0 z-[72] flex items-end sm:items-start lg:items-center justify-center overflow-y-auto bg-slate-900/25 backdrop-blur-sm p-3 sm:p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.28)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
        >
          <div
            className="my-3 w-full max-w-lg max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-2xl bg-gradient-to-br from-white via-slate-50 to-blue-50 p-4 shadow-xl sm:my-5 sm:max-h-[calc(100vh-2.5rem)] dark:from-gray-800 dark:via-gray-800 dark:to-slate-800 sm:p-6"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #f4fff8 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              {selectedClient.profileImage ? (
                <img
                  src={selectedClient.profileImage}
                  alt={selectedClient.name || "client"}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white">
                  {getInitials(selectedClient.name)}
                </div>
              )}
              <div className="min-w-0">
                <h3 className="truncate text-lg font-bold text-navy-700 dark:text-white">{selectedClient.name || "Unknown Client"}</h3>
                <p className="truncate text-sm text-gray-600 dark:text-gray-300">{selectedClient.phone || "No phone"}</p>
                {selectedClient.email && (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{selectedClient.email}</p>
                )}
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-gray-50 p-3 dark:bg-gray-700/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Calling Details</p>
              <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-gray-700 dark:text-gray-200 sm:grid-cols-2">
                <p><span className="font-semibold">Source:</span> {selectedClient.source || "-"}</p>
                <p><span className="font-semibold">Status:</span> {selectedClient.status || "-"}</p>
                <p><span className="font-semibold">Priority:</span> {selectedClient.priority || "-"}</p>
                <p className="sm:col-span-2">
                  <span className="font-semibold">Call Status:</span> {getCallingStatus(selectedClient) || "-"}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white/70 p-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Update Call Status</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={() => updateCallStatus("pending", { closeOnSuccess: true })}
                  disabled={isUpdatingCallStatus}
                  className="min-w-[118px] rounded-lg bg-amber-500 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-amber-600 disabled:opacity-60"
                >
                  {isUpdatingCallStatus ? "Updating..." : "Cancel Call"}
                </button>
                <button
                  onClick={() => updateCallStatus("completed", { closeOnSuccess: true })}
                  disabled={isUpdatingCallStatus}
                  className="min-w-[118px] rounded-lg bg-red-600 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
                >
                  {isUpdatingCallStatus ? "Updating..." : "Mark Completed"}
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => launchDialer(selectedClient.phone)}
                className="w-full rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 sm:w-auto"
              >
                Call Again
              </button>
              <button
                onClick={openActivityModal}
                className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 sm:w-auto"
              >
                Add Notes & Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {isActivityModalOpen && selectedClient && (
        <div
          className="fixed inset-0 z-[73] flex items-end sm:items-start lg:items-center justify-center overflow-y-auto bg-slate-900/25 backdrop-blur-sm p-3 sm:p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.28)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={closeCallFlow}
        >
          <div
            className="my-3 w-full max-w-2xl max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-2xl bg-gradient-to-br from-white via-slate-50 to-emerald-50 p-4 shadow-xl sm:my-5 sm:max-h-[calc(100vh-2.5rem)] dark:from-gray-800 dark:via-gray-800 dark:to-slate-800 sm:p-6"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #f1fff7 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Call Notes & Next Schedule</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{selectedClient.name || "Client"} • {selectedClient.phone || "No phone"}</p>
              </div>
              <button
                onClick={() => launchDialer(selectedClient.phone)}
                className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600"
              >
                Call
              </button>
            </div>

            {isFetchingActivityDetails ? (
              <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50/60 p-2 text-xs font-medium text-brand-700 dark:border-brand-400/40 dark:bg-brand-500/10 dark:text-brand-200">
                Loading latest notes and schedules...
              </div>
            ) : null}

            {activityFetchError ? (
              renderDismissibleLabel({
                message: activityFetchError,
                type: "error",
                onClose: () => setActivityFetchError(""),
                className: "mt-3",
              })
            ) : null}

            {activityDeleteStatus.message ? (
              renderDismissibleLabel({
                message: activityDeleteStatus.message,
                type: activityDeleteStatus.type === "error" ? "error" : "success",
                onClose: () => setActivityDeleteStatus({ type: "", message: "" }),
                className: "mt-3",
              })
            ) : null}

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <p className="text-sm font-semibold text-navy-700 dark:text-white">Add Call Note</p>
                {noteErrors.submit ? (
                  renderDismissibleLabel({
                    message: noteErrors.submit,
                    type: "error",
                    onClose: () =>
                      setNoteErrors((prev) => ({
                        ...prev,
                        submit: "",
                      })),
                  })
                ) : null}
                {noteErrors.clientId ? (
                  <p className="mt-2 text-xs text-red-500">{noteErrors.clientId}</p>
                ) : null}
                {noteSuccessMessage ? (
                  renderDismissibleLabel({
                    message: noteSuccessMessage,
                    type: "success",
                    onClose: () => setNoteSuccessMessage(""),
                  })
                ) : null}
                <textarea
                  value={noteInput}
                  onChange={(e) => {
                    setNoteInput(e.target.value);
                    setNoteSuccessMessage("");
                    setNoteErrors((prev) => ({
                      ...prev,
                      note: "",
                      submit: "",
                    }));
                  }}
                  placeholder="Write what happened in this call..."
                  className="mt-2 h-24 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {noteErrors.note ? (
                  <p className="mt-1 text-xs text-red-500">{noteErrors.note}</p>
                ) : null}
                <button
                  onClick={addNote}
                  disabled={isAddingNote}
                  className="mt-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                >
                  {isAddingNote ? "Adding..." : "Add Note"}
                </button>

                <div className="mt-3 max-h-44 overflow-y-auto rounded-lg bg-gray-50 p-2 dark:bg-gray-700/40">
                  {currentClientActivity.notes.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-300">No previous notes yet.</p>
                  ) : (
                    currentClientActivity.notes.map((note) => (
                      <div key={note.id} className="mb-2 rounded-lg bg-white p-2 text-sm dark:bg-gray-700">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-gray-700 dark:text-gray-100">{note.content}</p>
                          <button
                            onClick={() => requestActivityDelete({ type: "note", itemId: note.noteId || note.id })}
                            disabled={isDeletingActivity}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            title="Delete note"
                            aria-label="Delete note"
                          >
                            {isDeletingActivity && deletingActivityId === (note.noteId || note.id) ? "..." : "x"}
                          </button>
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-300">
                          {new Date(note.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <p className="flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-white">
                  <IoCalendar className="h-4 w-4" />
                  Schedule Next Call
                </p>
                <div className="mt-2 rounded-lg border border-dashed border-brand-300 bg-brand-50/40 p-3 dark:border-brand-400/40 dark:bg-brand-500/10">
                  {scheduleErrors.submit ? (
                    renderDismissibleLabel({
                      message: scheduleErrors.submit,
                      type: "error",
                      onClose: () =>
                        setScheduleErrors((prev) => ({
                          ...prev,
                          submit: "",
                        })),
                      className: "mb-2",
                    })
                  ) : null}
                  {scheduleSuccessMessage ? (
                    renderDismissibleLabel({
                      message: scheduleSuccessMessage,
                      type: "success",
                      onClose: () => setScheduleSuccessMessage(""),
                      className: "mb-2",
                    })
                  ) : null}
                  {scheduleErrors.clientId ? (
                    <p className="mb-2 text-xs text-red-500">{scheduleErrors.clientId}</p>
                  ) : null}
                  <input
                    type="datetime-local"
                    value={scheduleDateTime}
                    onChange={(e) => {
                      setScheduleDateTime(e.target.value);
                      setScheduleSuccessMessage("");
                      setScheduleErrors((prev) => ({
                        ...prev,
                        scheduledTime: "",
                        submit: "",
                      }));
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {scheduleErrors.scheduledTime ? (
                    <p className="mt-1 text-xs text-red-500">{scheduleErrors.scheduledTime}</p>
                  ) : null}
                  <input
                    type="text"
                    value={scheduleNote}
                    onChange={(e) => {
                      setScheduleNote(e.target.value);
                      setScheduleSuccessMessage("");
                      setScheduleErrors((prev) => ({
                        ...prev,
                        note: "",
                        submit: "",
                      }));
                    }}
                    placeholder="Optional note for next call"
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {scheduleErrors.note ? (
                    <p className="mt-1 text-xs text-red-500">{scheduleErrors.note}</p>
                  ) : null}
                  <button
                    onClick={addSchedule}
                    disabled={isAddingSchedule}
                    className="mt-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                  >
                    {isAddingSchedule ? "Adding..." : "Add Schedule"}
                  </button>
                </div>

                <div className="mt-3 max-h-44 overflow-y-auto rounded-lg bg-gray-50 p-2 dark:bg-gray-700/40">
                  {currentClientActivity.schedules.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-300">No previous schedules yet.</p>
                  ) : (
                    currentClientActivity.schedules.map((item) => (
                      <div key={item.id} className="mb-2 rounded-lg bg-white p-2 text-sm dark:bg-gray-700">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-gray-800 dark:text-gray-100">
                            {new Date(item.scheduledFor).toLocaleString()}
                          </p>
                          <button
                            onClick={() =>
                              requestActivityDelete({ type: "schedule", itemId: item.scheduleId || item.id })
                            }
                            disabled={isDeletingActivity}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            title="Delete schedule"
                            aria-label="Delete schedule"
                          >
                            {isDeletingActivity && deletingActivityId === (item.scheduleId || item.id)
                              ? "..."
                              : "x"}
                          </button>
                        </div>
                        {item.note ? (
                          <p className="mt-1 text-gray-600 dark:text-gray-200">{item.note}</p>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => {
                  setIsActivityModalOpen(false);
                  setIsCallerModalOpen(true);
                }}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Back
              </button>
              <button
                onClick={() => updateCallStatus("completed", { closeOnSuccess: true })}
                disabled={isUpdatingCallStatus}
                className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 sm:w-auto"
              >
                {isUpdatingCallStatus ? "Updating..." : "Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityDeleteConfirm.show && (
        <div
          className="fixed inset-0 z-[74] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.32)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={closeActivityDeleteConfirm}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-gradient-to-br from-white via-slate-50 to-rose-50 p-5 shadow-xl dark:from-gray-800 dark:via-gray-800 dark:to-slate-800"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #fff4f4 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">{activityDeleteConfirm.title}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{activityDeleteConfirm.message}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This action cannot be undone.</p>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeActivityDeleteConfirm}
                disabled={isDeletingActivity}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmActivityDelete}
                disabled={isDeletingActivity}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 sm:w-auto"
              >
                {isDeletingActivity ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {clientDeleteConfirm.show && (
        <div
          className="fixed inset-0 z-[74] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.32)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={closeClientDeleteConfirm}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-gradient-to-br from-white via-slate-50 to-rose-50 p-5 shadow-xl dark:from-gray-800 dark:via-gray-800 dark:to-slate-800"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #fff4f4 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete Client?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to delete <span className="font-semibold">{clientDeleteConfirm.clientName}</span>?
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This action cannot be undone.</p>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeClientDeleteConfirm}
                disabled={isDeletingClient}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmClientDelete}
                disabled={isDeletingClient}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60 sm:w-auto"
              >
                {isDeletingClient ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
